import { getUsersNotLoggedReport } from "@/services/accessReport";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";


const PAGE_SIZE = 100;

const AccessReport = () => {

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1); 
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);

      try {
        const data =
          await getUsersNotLoggedReport();

        setUsers(data);
      } catch (error) {
        console.error(
          "Failed to load report",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, []);

  const filteredUsers =
    useMemo(() => {
      return users.filter((user) => {
        const term =
          search.toLowerCase();

        return (
          (
            user.DisplayName || ""
          )
            .toLowerCase()
            .includes(term) ||
          (
            user.Email || ""
          )
            .toLowerCase()
            .includes(term) ||
          (
            user.UserPrincipalName ||
            ""
          )
            .toLowerCase()
            .includes(term)
        );
      });
    }, [users, search]);


  const pagedUsers =
    useMemo(() => {
      return filteredUsers.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
      );
    }, [filteredUsers, page]);

  const downloadCsv = () => {
    const rows =
      filteredUsers.map((user) => ({
        Name:
          user.DisplayName || "",
        Email: user.Email || "",
        UserPrincipalName:
          user.UserPrincipalName || "",
        Status:
          "Never Logged In",
      }));

    const headers =
      Object.keys(rows[0] || {});

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map(
            (header) =>
              `"${String(
                row[
                  header as keyof typeof row
                ] ?? ""
              ).replace(/"/g, '""')}"`
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob(
      [csvContent],
      {
        type: "text/csv;charset=utf-8;",
      }
    );

    const link =
      document.createElement("a");

    link.href =
      URL.createObjectURL(blob);

    link.download = `Partner_Access_Report_${new Date()
      .toISOString()
      .split("T")[0]}.csv`;

    link.click();
  };


  const ReportRowSkeleton = () => (
  <div className="border border-gray-200 rounded-md p-4 animate-pulse">
    <div className="flex justify-between items-center">
      <div className="flex-1">
        <div className="h-4 w-48 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-64 bg-gray-200 rounded" />

        <div className="mt-3">
          <div className="h-5 w-24 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  </div>
);

  return (
    <div className="bg-[#f6f4ef] min-h-screen font-sans">
      <div className="p-5">
        <div className="bg-white border border-gray-200 rounded-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-md font-semibold text-gray-800">
              Partner Access Report
            </h2>

           <div className="flex items-center gap-3">

  <button
    onClick={downloadCsv}
    disabled={!filteredUsers.length}
    className="bg-[var(--color-brand)] text-white px-4 py-2 rounded-md text-sm disabled:bg-gray-300"
  >
    Download Report
  </button>
</div>
          </div>

          <div className="p-5">
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search users by name or email..."
                className="w-full border border-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-400"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            {loading ? (
            <div className="space-y-3">
                <ReportRowSkeleton />
                <ReportRowSkeleton />
                <ReportRowSkeleton />
                <ReportRowSkeleton />
                <ReportRowSkeleton />
            </div>
            ) : (
              <div className="space-y-3">
                {pagedUsers.length > 0 ? (
                  pagedUsers.map((user) => (
                    <div
                      key={user.UserPrincipalName}
                      className="border border-gray-200 rounded-md p-4 flex justify-between items-center"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          {user.DisplayName || "N/A"}
                        </div>

                        <div className="text-xs text-gray-500">
                          {user.Email || user.UserPrincipalName}
                        </div>

                        <div className="mt-2">
                          <span className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded">
                            Never Logged In
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-10 text-gray-500">
                    No users found.
                  </div>
                )}
              </div>
            )}
          </div>

          {!loading && filteredUsers.length > 0 && (
            <div className="flex items-center justify-center gap-4 p-5 border-t border-gray-200">
              <button
                className="bg-[var(--color-brand)] text-white px-3 py-1.5 rounded-md text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={page === 1}
                onClick={() =>
                  setPage((prev) => prev - 1)
                }
              >
                Previous
              </button>

              <span className="text-sm font-medium text-gray-700">
                Page {page} of{" "}
                {Math.max(
                  1,
                  Math.ceil(
                    filteredUsers.length /
                      PAGE_SIZE
                  )
                )}
              </span>

              <button
                className="bg-[var(--color-brand)] text-white px-3 py-1.5 rounded-md text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={
                  page >=
                  Math.ceil(
                    filteredUsers.length /
                      PAGE_SIZE
                  )
                }
                onClick={() =>
                  setPage((prev) => prev + 1)
                }
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessReport;